/** @format */

import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import { Fragment, StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

import App from "./App";
import consoleMessage from "./console";
import { APP_VERSION, SW_UPDATE_AVAILABLE_EVENT } from "./constants/version";
import "./index.css";
import type { PostHogInterface } from "posthog-js";
import type { RunDetail } from "./lib/schemas/apiSchemas";
import { queryClient } from "./queryClient";
import * as serviceWorker from "./serviceWorker";
import { initCacheUpdateListener } from "./utils/cacheUpdateListener";
import ErrorBoundary from "./utils/ErrorBoundary";
import { clearRefreshMarker, isChunkLoadError } from "./utils/lazyWithRetry";

// import axe from "@axe-core/react";

const enableStrictMode =
  import.meta.env.PROD || import.meta.env.VITE_ENABLE_STRICT_MODE === "true";
const RootMode = enableStrictMode ? StrictMode : Fragment;

console.info(consoleMessage);

function runWhenIdle(callback: IdleRequestCallback, timeout = 2000) {
  // requestIdleCallback is not supported in all browsers (e.g., some Safari versions)
  if (typeof window.requestIdleCallback === "function") {
    return window.requestIdleCallback(callback, { timeout });
  }

  // Fallback to setTimeout to ensure deferred work still runs
  return window.setTimeout(
    () =>
      callback({
        didTimeout: true,
        timeRemaining: () => 0,
      } as IdleDeadline),
    timeout
  );
}

// if (isDev) {
//   axe(React, ReactDOM, 1000);
// }

// Debug PostHog environment variables in development
if (import.meta.env.DEV) {
}

const options = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  ui_host: "https://us.posthog.com",
  capture_pageview: import.meta.env.VITE_LOCAL !== "True",
  capture_pageleave: import.meta.env.VITE_LOCAL !== "True",
  autocapture: import.meta.env.VITE_LOCAL !== "True",
  disable_session_recording: import.meta.env.VITE_LOCAL === "True",
  enable_recording_console_log: import.meta.env.VITE_LOCAL !== "True",
  enable_recording_network_payloads: import.meta.env.VITE_LOCAL !== "True",
  loaded: (posthog: PostHogInterface) => {
    if (import.meta.env.DEV) {
      // Expose the SDK on window so dev-console diagnostics work — handy
      // when chasing flag/payload mismatches that need reloadFeatureFlags.
      (window as unknown as { posthog: PostHogInterface }).posthog = posthog;
    }
  },
};

const rootElement = document.getElementById("root");

if (rootElement === null) {
  throw new Error("No root element found");
}

const root = ReactDOM.createRoot(rootElement);

const FallbackComponent = () => <ErrorBoundary />;

const MOCK_LIVE_RUN_STORAGE_KEY = "galaxieMockLiveRuns";

const mockLiveRuns: RunDetail[] = [
  {
    id: "mock-live-1",
    nascarId: 999999,
    series: 1,
    name: "Daytona Test",
    type: 1,
    startTime: new Date().toISOString(),
    endTime: new Date().toISOString(),
    totalLaps: 200,
    scheduledLaps: 200,
    actualLaps: 200,
    scheduledDistance: 500,
    actualDistance: 500,
    stageLaps: 60,
    stageStart: 80,
    stageRemaining: 20,
    stageCompleted: false,
    stageEnd: 100,
    track: "Daytona",
    lat: 0,
    lng: 0,
    trackType: "Superspeedway",
    trackTz: "America/New_York",
    pitStopDelta: 12.5,
    trackMap: null,
    lapNumber: 80,
    lapId: "lap-80",
    elapsedTime: 3600,
    length: null,
    flag: 1,
    flagPeriods: [],
    stages: [],
    currentStage: "Stage 2",
    stageKey: "stage-2",
    lapsRemaining: 120,
    seriesSupportedFeatures: [],
    seriesSanctioningBody: "",
  },
];

function maybeSeedMockLiveRuns() {
  if (typeof window === "undefined") {
    return;
  }
  const shouldMock = localStorage.getItem(MOCK_LIVE_RUN_STORAGE_KEY) === "true";
  if (!shouldMock) {
    return;
  }

  queryClient.setQueryData(["liveRuns"], {
    data: mockLiveRuns,
    version: "mock-local",
  });

  console.info(
    "[Galaxie] Using mocked live runs from localStorage:",
    MOCK_LIVE_RUN_STORAGE_KEY
  );
}

// Render app immediately - don't wait for analytics
maybeSeedMockLiveRuns();
root.render(
  <RootMode>
    <PostHogProvider options={options} apiKey={import.meta.env.VITE_POSTHOG_CLIENT_ID}>
      <Sentry.ErrorBoundary fallback={FallbackComponent}>
        <App />
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </RootMode>
);

// Track Sentry initialization state to prevent multiple initializations
let sentryInitialized = false;

// Initialize analytics after app renders to avoid blocking main thread
function initAnalytics() {
  if (import.meta.env.VITE_LOCAL !== "True") {
    // Guard against multiple Sentry initializations (e.g., from React StrictMode or HMR)
    if (!sentryInitialized) {
      try {
        Sentry.init({
          dsn: import.meta.env.VITE_SENTRY_DSN,
          release: APP_VERSION,
          integrations: [
            Sentry.browserTracingIntegration({
              // Don't trace every transaction - focus on errors
              traceFetch: false,
              traceXHR: false,
            }),
            Sentry.replayIntegration({
              maskAllText: true, // Privacy: mask user text
              blockAllMedia: true, // Don't record media
            }),
          ],
          // Reduced from 1.0 (100%) to 0.1 (10%) for performance
          tracesSampleRate: 0.1,
          tracePropagationTargets: ["localhost", /^https:\/\/api.galaxie\.app\/api/],
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
          enabled: true,
          beforeSend(event, hint) {
            const error = hint?.originalException;
            // Filter chunk load errors — lazyWithRetry handles recovery
            if (error instanceof Error && isChunkLoadError(error)) return null;
            // Filter common browser noise patterns
            const message = event.exception?.values?.[0]?.value ?? "";
            const NOISE = [
              /ResizeObserver loop/i,
              /Non-Error promise rejection captured/i,
              /AbortError/i,
              /NotAllowedError.*play/i,
              /Load failed/i,
              // Crypto wallet extensions injecting into our window — user-side,
              // not our bug (JAVASCRIPT-REACT-JS, ~2 events).
              /Failed to connect to MetaMask/i,
              // Expected when a session expires before the user notices; the
              // auth client redirects to /login. PostHog 019e2dae / Sentry
              // JAVASCRIPT-REACT-JT. TODO: catch this at the source in the
              // refresh-token call site so we don't capture at all.
              /Refresh token was not included in cookie data/i,
            ];
            if (NOISE.some(p => p.test(message))) return null;
            return event;
          },
        });
        sentryInitialized = true;
      } catch (error) {
        // If Sentry is already initialized, catch the error and mark as initialized
        // This prevents the "Multiple Sentry Session Replay instances" error

        if (import.meta.env.DEV) {
          console.warn("Sentry initialization skipped (already initialized):", error);
        }
        sentryInitialized = true;
      }
    }
  }

  // Set up global error handlers after Sentry is initialized
  setupGlobalErrorHandlers();
}

/**
 * Sets up global error handlers for unhandled errors and promise rejections
 */
function setupGlobalErrorHandlers() {
  // Clear the chunk refresh marker on successful page load
  // This allows future chunk errors to trigger a refresh
  clearRefreshMarker();

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", event => {
    const error: unknown = event.reason;

    // Chunk load errors — lazyWithRetry handles refresh, beforeSend filters from Sentry
    if (isChunkLoadError(error)) {
      event.preventDefault();
      return;
    }

    // Log to Sentry
    Sentry.captureException(error, {
      tags: { errorType: "unhandled_promise_rejection" },
      extra: {
        promise:
          typeof error === "string"
            ? error
            : error instanceof Error
              ? error.message
              : "Unknown rejection",
      },
    });

    // Log to console for debugging

    console.error("Unhandled promise rejection:", error);

    // Prevent default browser error handling in production
    // In development, we might want to see the error in console
    if (!import.meta.env.DEV) {
      event.preventDefault();
    }
  });

  // Handle uncaught errors (outside React error boundaries)
  window.addEventListener("error", event => {
    const error = (event.error as Error | undefined) ?? new Error(event.message);

    // Chunk load errors — beforeSend filters these from Sentry
    if (isChunkLoadError(error)) {
      return;
    }

    // Log to Sentry
    Sentry.captureException(error, {
      tags: { errorType: "uncaught_error" },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });

    // Log to console for debugging

    console.error("Uncaught error:", error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
}

/**
 * Reports Core Web Vitals metrics to PostHog and Sentry
 * Metrics tracked: LCP, INP (replaced FID), CLS, FCP, TTFB
 */
function reportWebVitals() {
  // Skip in local development
  if (import.meta.env.VITE_LOCAL === "True") {
    return;
  }

  const reportMetric = (metric: { name: string; value: number; rating: string }) => {
    // Report to PostHog
    const posthog = (
      window as unknown as {
        posthog?: { capture: (event: string, props: object) => void };
      }
    ).posthog;
    if (posthog) {
      posthog.capture("web_vital", {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_rating: metric.rating, // 'good', 'needs-improvement', 'poor'
      });
    }

    // Also report to Sentry as measurements
    // Use appropriate units: CLS is unitless, others are milliseconds
    const unit = metric.name === "CLS" ? "none" : "millisecond";
    Sentry.setMeasurement(`web_vital.${metric.name}`, metric.value, unit);
  };

  // Register callbacks for all Core Web Vitals
  onLCP(reportMetric); // Largest Contentful Paint
  onINP(reportMetric); // Interaction to Next Paint (replaced FID in 2024)
  onCLS(reportMetric); // Cumulative Layout Shift
  onFCP(reportMetric); // First Contentful Paint
  onTTFB(reportMetric); // Time to First Byte
}

// Defer analytics initialization until after page is interactive
runWhenIdle(() => {
  initAnalytics();
}, 2000);

// Report Core Web Vitals after page is fully loaded
runWhenIdle(() => {
  reportWebVitals();
}, 3000);

// Initialize service worker cache update listener
// This listens for BroadcastChannel messages from the SW's BroadcastUpdatePlugin
// and invalidates React Query cache when SW cache is updated in background
runWhenIdle(() => {
  initCacheUpdateListener(queryClient);
}, 3000);

// Defer service worker registration until after React has fully initialized
// This prevents service worker from interfering with React's initial render
// and causing "Invalid hook call" errors
runWhenIdle(() => {
  // Additional delay to ensure React Query and all providers are initialized
  setTimeout(() => {
    // PWA enabled - Service worker will cache assets for offline support
    // and handle app updates via UpdateToast component
    serviceWorker.register({
      onUpdate: (_registration: ServiceWorkerRegistration) => {
        // Registration is already stored by serviceWorker.js — just signal the update
        window.dispatchEvent(new Event(SW_UPDATE_AVAILABLE_EVENT));
      },
      onSuccess: (_registration: ServiceWorkerRegistration) => {},
    });
  }, 1000); // 1 second delay after idle to ensure React is fully initialized
}, 3000); // Wait up to 3 seconds for idle time

// Swap static logo to animated version after page is interactive
// This prevents the animated SVG from blocking LCP
function swapLogoToAnimated() {
  const logoEl = document.getElementById("galaxie-loader-logo");
  if (logoEl) {
    logoEl.setAttribute("src", "/src/assets/galaxie_logo.svg");
  }
}

if (document.readyState === "complete") {
  swapLogoToAnimated();
} else {
  window.addEventListener("load", () => {
    runWhenIdle(() => swapLogoToAnimated(), 2000);
  });
}
